import { useState, useCallback } from "react";
import {
  Modal,
  Stack,
  Text,
  Button,
  Group,
  Paper,
  Table,
  FileButton,
  Alert,
  Progress,
  Badge,
  Anchor,
} from "@mantine/core";
import {
  IconUpload,
  IconDownload,
  IconAlertCircle,
  IconCheck,
  IconX,
} from "@tabler/icons-react";
import Papa from "papaparse";
import { useCostCodes } from "@/hooks/useCostCodes";
import { useBulkImportBudget, type BulkImportBudgetLineData } from "@/hooks/useBudget";
import { notifications } from "@mantine/notifications";

interface BudgetImportModalProps {
  opened: boolean;
  onClose: () => void;
  projectId: string;
}

interface CSVRow {
  description: string;
  cost_code?: string;
  costCode?: string;
  quantity?: string;
  unit?: string;
  unit_cost?: string;
  unitCost?: string;
  total?: string;
}

interface ParsedRow {
  row: number;
  description: string;
  costCode: string;
  quantity: number;
  unit: string;
  unitCost: number;
  total: number;
  costCodeId?: string;
  errors: string[];
  warnings: string[];
}

const CSV_TEMPLATE = `description,cost_code,quantity,unit,unit_cost,total
Foundation concrete,03 30 00,100,CY,150,15000
Rebar installation,03 20 00,2000,LF,2.50,5000
Formwork,03 11 00,500,SF,3.00,1500`;

export function BudgetImportModal({ opened, onClose, projectId }: BudgetImportModalProps) {
  const { data: costCodesData } = useCostCodes();
  const importMutation = useBulkImportBudget(projectId);

  const [parsedRows, setParsedRows] = useState<ParsedRow[]>([]);
  const [previewRows, setPreviewRows] = useState<ParsedRow[]>([]);
  const [step, setStep] = useState<"upload" | "preview" | "mapping" | "complete">("upload");
  const [isProcessing, setIsProcessing] = useState(false);

  const costCodes = costCodesData?.costCodes || [];

  const downloadTemplate = () => {
    const blob = new Blob([CSV_TEMPLATE], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "budget_template.csv";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const matchCostCode = (code: string): string | undefined => {
    // Normalize the code by removing extra spaces
    const normalized = code.trim().replace(/\s+/g, " ");

    // Exact match
    const exactMatch = costCodes.find((cc) => cc.code === normalized);
    if (exactMatch) return exactMatch.id;

    // Try fuzzy match (remove spaces completely)
    const noSpaces = normalized.replace(/\s/g, "");
    const fuzzyMatch = costCodes.find((cc) => cc.code.replace(/\s/g, "") === noSpaces);
    if (fuzzyMatch) return fuzzyMatch.id;

    return undefined;
  };

  const parseCSV = useCallback(
    (csvFile: File) => {
      setIsProcessing(true);
      Papa.parse<CSVRow>(csvFile, {
        header: true,
        skipEmptyLines: true,
        complete: (results) => {
          const rows: ParsedRow[] = results.data.map((row, index) => {
            const errors: string[] = [];
            const warnings: string[] = [];

            // Get cost code from either cost_code or costCode column
            const costCodeStr = (row.cost_code || row.costCode || "").trim();
            const description = (row.description || "").trim();
            const quantityStr = row.quantity || "";
            const unit = (row.unit || "").trim();
            const unitCostStr = row.unit_cost || row.unitCost || "";
            const totalStr = row.total || "";

            // Required field validation
            if (!description) errors.push("Description is required");
            if (!costCodeStr) errors.push("Cost code is required");
            if (!quantityStr) errors.push("Quantity is required");
            if (!unit) errors.push("Unit is required");

            // Parse numbers
            const quantity = parseFloat(quantityStr);
            const unitCost = parseFloat(unitCostStr);
            const total = parseFloat(totalStr);

            // Validate numbers
            if (quantityStr && isNaN(quantity)) {
              errors.push("Quantity must be a number");
            } else if (quantity <= 0) {
              errors.push("Quantity must be greater than 0");
            }

            if (unitCostStr && isNaN(unitCost)) {
              errors.push("Unit cost must be a number");
            } else if (unitCost < 0) {
              errors.push("Unit cost cannot be negative");
            }

            if (totalStr && isNaN(total)) {
              errors.push("Total must be a number");
            } else if (total <= 0) {
              errors.push("Total must be greater than 0");
            }

            // Match cost code
            const costCodeId = matchCostCode(costCodeStr);
            if (costCodeStr && !costCodeId) {
              errors.push(`Cost code "${costCodeStr}" not found`);
            }

            // Calculate total if not provided
            let finalTotal = total;
            if (!totalStr && !isNaN(quantity) && !isNaN(unitCost)) {
              finalTotal = quantity * unitCost;
            }

            // Verify calculation
            if (
              !isNaN(quantity) &&
              !isNaN(unitCost) &&
              !isNaN(total) &&
              Math.abs(quantity * unitCost - total) > 0.01
            ) {
              warnings.push(
                `Total (${total}) doesn't match quantity × unit cost (${quantity * unitCost}). Will use provided total.`
              );
            }

            return {
              row: index + 1,
              description,
              costCode: costCodeStr,
              quantity,
              unit,
              unitCost,
              total: finalTotal,
              costCodeId,
              errors,
              warnings,
            };
          });

          setParsedRows(rows);
          setPreviewRows(rows.slice(0, 5));
          setStep("preview");
          setIsProcessing(false);
        },
        error: (error) => {
          notifications.show({
            title: "Error",
            message: `Failed to parse CSV: ${error.message}`,
            color: "red",
          });
          setIsProcessing(false);
        },
      });
    },
    [costCodes]
  );

  const handleFileSelect = (selectedFile: File | null) => {
    if (!selectedFile) return;

    if (!selectedFile.name.endsWith(".csv")) {
      notifications.show({
        title: "Error",
        message: "Please select a CSV file",
        color: "red",
      });
      return;
    }

    parseCSV(selectedFile);
  };

  const handleImport = async () => {
    const validRows = parsedRows.filter((row) => row.errors.length === 0 && row.costCodeId);

    if (validRows.length === 0) {
      notifications.show({
        title: "Error",
        message: "No valid rows to import",
        color: "red",
      });
      return;
    }

    const lines: BulkImportBudgetLineData[] = validRows.map((row) => ({
      costCodeId: row.costCodeId!,
      description: row.description,
      quantity: row.quantity,
      unit: row.unit,
      unitCost: row.unitCost,
    }));

    try {
      const result = await importMutation.mutateAsync({ lines });
      notifications.show({
        title: "Success",
        message: `Imported ${result.created} budget lines!`,
        color: "green",
      });
      setStep("complete");
    } catch (error) {
      notifications.show({
        title: "Error",
        message: error instanceof Error ? error.message : "Failed to import budget",
        color: "red",
      });
    }
  };

  const handleClose = () => {
    setParsedRows([]);
    setPreviewRows([]);
    setStep("upload");
    setIsProcessing(false);
    onClose();
  };

  const errorCount = parsedRows.filter((row) => row.errors.length > 0).length;
  const warningCount = parsedRows.filter((row) => row.warnings.length > 0).length;
  const validCount = parsedRows.filter((row) => row.errors.length === 0).length;

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 2,
    }).format(amount);
  };

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      title="Import Budget from CSV"
      size="xl"
      centered
    >
      <Stack gap="lg">
        {/* Template download */}
        {step === "upload" && (
          <Alert icon={<IconAlertCircle />} color="blue" variant="light">
            <Text size="sm" mb="xs">
              Download the template to ensure your CSV has the correct format.
            </Text>
            <Anchor onClick={downloadTemplate} size="sm">
              <Group gap="xs">
                <IconDownload size={14} />
                Download CSV Template
              </Group>
            </Anchor>
          </Alert>
        )}

        {/* File upload */}
        {step === "upload" && (
          <Paper p="xl" withBorder style={{ borderStyle: "dashed" }}>
            <Stack gap="md" align="center">
              <IconUpload size={48} style={{ opacity: 0.5 }} />
              <Text size="sm" c="dimmed" ta="center">
                Drag and drop a CSV file here, or click to select
              </Text>
              <FileButton onChange={handleFileSelect} accept=".csv">
                {(props) => (
                  <Button {...props} leftSection={<IconUpload size={16} />}>
                    Select CSV File
                  </Button>
                )}
              </FileButton>
            </Stack>
          </Paper>
        )}

        {/* Processing indicator */}
        {isProcessing && (
          <Stack gap="md">
            <Text size="sm">Processing CSV file...</Text>
            <Progress value={100} animated />
          </Stack>
        )}

        {/* Preview and validation */}
        {step === "preview" && (
          <Stack gap="md">
            <Group justify="space-between">
              <Text size="sm" fw={500}>
                Found {parsedRows.length} rows
              </Text>
              <Group gap="xs">
                {validCount > 0 && (
                  <Badge color="green" leftSection={<IconCheck size={12} />}>
                    {validCount} valid
                  </Badge>
                )}
                {errorCount > 0 && (
                  <Badge color="red" leftSection={<IconX size={12} />}>
                    {errorCount} errors
                  </Badge>
                )}
                {warningCount > 0 && (
                  <Badge color="yellow" leftSection={<IconAlertCircle size={12} />}>
                    {warningCount} warnings
                  </Badge>
                )}
              </Group>
            </Group>

            {errorCount > 0 && (
              <Alert icon={<IconAlertCircle />} color="red" variant="light">
                <Text size="sm">
                  Found {errorCount} row{errorCount > 1 ? "s" : ""} with errors. These rows will
                  be skipped during import.
                </Text>
              </Alert>
            )}

            <Paper withBorder>
              <Table striped highlightOnHover>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th style={{ width: "40px" }}>Row</Table.Th>
                    <Table.Th>Cost Code</Table.Th>
                    <Table.Th>Description</Table.Th>
                    <Table.Th style={{ textAlign: "right" }}>Qty</Table.Th>
                    <Table.Th>Unit</Table.Th>
                    <Table.Th style={{ textAlign: "right" }}>Unit Cost</Table.Th>
                    <Table.Th style={{ textAlign: "right" }}>Total</Table.Th>
                    <Table.Th style={{ width: "40px" }}></Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {previewRows.map((row) => (
                    <Table.Tr
                      key={row.row}
                      style={{
                        backgroundColor: row.errors.length > 0 ? "var(--mantine-color-red-0)" : undefined,
                      }}
                    >
                      <Table.Td>
                        <Text size="xs" c="dimmed">
                          {row.row}
                        </Text>
                      </Table.Td>
                      <Table.Td>
                        <Text size="sm" fw={row.costCodeId ? 500 : undefined} c={row.costCodeId ? undefined : "red"}>
                          {row.costCode}
                        </Text>
                      </Table.Td>
                      <Table.Td>
                        <Text size="sm">{row.description}</Text>
                        {row.errors.length > 0 && (
                          <Text size="xs" c="red">
                            {row.errors.join(", ")}
                          </Text>
                        )}
                        {row.warnings.length > 0 && (
                          <Text size="xs" c="yellow">
                            {row.warnings.join(", ")}
                          </Text>
                        )}
                      </Table.Td>
                      <Table.Td style={{ textAlign: "right" }}>
                        <Text size="sm">{isNaN(row.quantity) ? "-" : row.quantity}</Text>
                      </Table.Td>
                      <Table.Td>
                        <Text size="sm">{row.unit}</Text>
                      </Table.Td>
                      <Table.Td style={{ textAlign: "right" }}>
                        <Text size="sm">{isNaN(row.unitCost) ? "-" : formatCurrency(row.unitCost)}</Text>
                      </Table.Td>
                      <Table.Td style={{ textAlign: "right" }}>
                        <Text size="sm" fw={600}>
                          {isNaN(row.total) ? "-" : formatCurrency(row.total)}
                        </Text>
                      </Table.Td>
                      <Table.Td>
                        {row.errors.length === 0 && row.costCodeId ? (
                          <IconCheck size={16} color="green" />
                        ) : (
                          <IconX size={16} color="red" />
                        )}
                      </Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            </Paper>

            {parsedRows.length > 5 && (
              <Text size="xs" c="dimmed" ta="center">
                Showing first 5 rows. {parsedRows.length - 5} more rows will be imported.
              </Text>
            )}

            <Group justify="flex-end" mt="md">
              <Button variant="subtle" onClick={handleClose}>
                Cancel
              </Button>
              <Button
                onClick={handleImport}
                loading={importMutation.isPending}
                disabled={validCount === 0}
              >
                Import {validCount} Row{validCount !== 1 ? "s" : ""}
              </Button>
            </Group>
          </Stack>
        )}

        {/* Success state */}
        {step === "complete" && (
          <Stack gap="md" align="center">
            <IconCheck size={64} color="green" />
            <Text size="lg" fw={500}>
              Import Complete!
            </Text>
            <Text size="sm" c="dimmed">
              Successfully imported {validCount} budget lines
            </Text>
            <Button onClick={handleClose}>Close</Button>
          </Stack>
        )}
      </Stack>
    </Modal>
  );
}
